"use client";

import { useSearchParams, useRouter } from "next/navigation";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { Banner, Button, InputText } from "@/design-system";
import { LoginProgressIndicator } from "./components/LoginProgressIndicator";
import { useAuth } from "@/contexts/AuthContext";
import type { BrowserAuthResponse, LoginOptionsResponse, MagicLinkPreviewResponse } from "@/types/auth";
import { buildLoginSetupPasskeyUrl } from "@/lib/loginSetupPasskeyRoute";
import { markPasskeySetupCompleted } from "@/lib/passkeySetupDeferral";
import { normalizeEmailAddress } from "@/utils/email";
import styles from "./page.module.css";

type ButtonState = "idle" | "processing" | "success" | "error";
type EmailLoginStep = "email" | "options";

function LoginContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { requestMagicLink, login, user, discoverLoginOptions, completeAuthResponse } = useAuth();
  const [isExiting, setIsExiting] = useState(false);
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [error, setError] = useState(searchParams.get("oauthError") || "");
  const [buttonState, setButtonState] = useState<ButtonState>("idle");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [processingMagicLink, setProcessingMagicLink] = useState(false);
  const [previewingMagicLink, setPreviewingMagicLink] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [step, setStep] = useState<EmailLoginStep>("email");
  const [loginOptions, setLoginOptions] = useState<LoginOptionsResponse | null>(null);
  const [magicLinkPreview, setMagicLinkPreview] = useState<MagicLinkPreviewResponse | null>(null);
  const previewedMagicLinkTokenRef = useRef<string | null>(null);
  const authRedirectStartedRef = useRef(false);

  const requestedRedirectTarget = searchParams.get("redirect") || "";
  const redirectTarget =
    requestedRedirectTarget.startsWith("/") && !requestedRedirectTarget.startsWith("//")
      ? requestedRedirectTarget
      : "/dashboard";
  const magicLinkToken = searchParams.get("magicLinkToken");

  const previewMagicLink = useCallback(
    async (magicLinkToken: string) => {
      if (previewedMagicLinkTokenRef.current === magicLinkToken) {
        return;
      }
      previewedMagicLinkTokenRef.current = magicLinkToken;
      setPreviewingMagicLink(true);
      setError("");
      setMagicLinkPreview(null);

      try {
        const response = await fetch("/api/users/preview-magic-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ magicLinkToken }),
        });
        const body = (await response.json().catch(() => ({}))) as Partial<MagicLinkPreviewResponse> & { error?: string };

        if (!response.ok) {
          setError(body.error || "Invalid or expired magic link. Please request a new one.");
          return;
        }

        setMagicLinkPreview(body as MagicLinkPreviewResponse);
      } catch {
        setError("An error occurred while checking the magic link. Please try again.");
      } finally {
        setPreviewingMagicLink(false);
      }
    },
    [],
  );

  const processMagicLink = useCallback(
    async (token: string) => {
      setProcessingMagicLink(true);
      setError("");

      try {
        authRedirectStartedRef.current = true;
        const success = await login("", "", token);
        if (success) {
          router.replace(buildLoginSetupPasskeyUrl(redirectTarget));
          return;
        }
        authRedirectStartedRef.current = false;
        setError("Invalid or expired magic link. Please request a new one.");
      } catch {
        authRedirectStartedRef.current = false;
        setError("An error occurred while processing the magic link. Please try again.");
      } finally {
        setProcessingMagicLink(false);
      }
    },
    [login, redirectTarget, router],
  );

  useEffect(() => {
    if (authRedirectStartedRef.current) return;
    if (user) {
      if (magicLinkToken) {
        return;
      }
      router.replace(redirectTarget);
      return;
    }
    if (magicLinkToken) {
      void previewMagicLink(magicLinkToken);
    }
  }, [magicLinkToken, previewMagicLink, redirectTarget, router, user]);

  useEffect(() => {
    setPasskeySupported(browserSupportsWebAuthn());
  }, []);

  const discoverOptions = useCallback(async () => {
    const normalizedEmail = normalizeEmailAddress(email);
    if (!normalizedEmail) {
      setError("Enter your email first.");
      return;
    }
    setDiscovering(true);
    setError("");
    const discovered = await discoverLoginOptions(normalizedEmail);
    if (!discovered) {
      setError("Could not load sign-in options for this email.");
      setDiscovering(false);
      return;
    }
    setLoginOptions(discovered);
    setMagicLinkSent(false);
    setStep("options");
    setDiscovering(false);
  }, [discoverLoginOptions, email]);

  const handleMagicLinkRequest = useCallback(async () => {
    const normalizedEmail = normalizeEmailAddress(email);
    if (!normalizedEmail) {
      setError("Enter your email first.");
      return;
    }

    setError("");
    setButtonState("processing");
    setMagicLinkSent(false);

    try {
      const success = await requestMagicLink(normalizedEmail);
      if (!success) {
        setButtonState("error");
        setError("Failed to send magic link. Please try again.");
        return;
      }
      setMagicLinkSent(true);
      setButtonState("success");
    } catch {
      setButtonState("error");
      setError("An error occurred while sending the magic link. Please try again.");
    } finally {
      window.setTimeout(() => setButtonState("idle"), 2500);
    }
  }, [email, requestMagicLink]);

  const handleExit = useCallback(() => {
    setIsExiting(true);
  }, []);

  const handleSwitchToPassword = useCallback(() => {
    handleExit();
    setTimeout(() => {
      router.push(`/login/password${email ? `?email=${encodeURIComponent(email)}` : ""}`);
    }, 300);
  }, [email, handleExit, router]);

  const magicLinkStateLabels = {
    idle: "Email me a sign-in link",
    processing: "Sending...",
    success: "Sent!",
    error: "Failed",
  } as const;

  const handleEmailStepSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (step === "options") {
        return;
      }

      const normalizedEmail = normalizeEmailAddress(email);
      const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
      if (!emailLooksValid || discovering || passkeyLoading) {
        return;
      }

      void discoverOptions();
    },
    [discoverOptions, discovering, email, passkeyLoading, step],
  );

  const handlePasskeyLogin = useCallback(async () => {
    const normalizedEmail = normalizeEmailAddress(email);
    if (!normalizedEmail) {
      setError("Enter your email first.");
      return;
    }
    if (!browserSupportsWebAuthn()) {
      setError("This browser does not support passkeys.");
      return;
    }

    setPasskeyLoading(true);
    setError("");
    try {
      const optionsResponse = await fetch("/api/auth/passkeys/login/options", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      if (!optionsResponse.ok) {
        const body = (await optionsResponse.json().catch(() => ({}))) as { error?: string };
        setError(body.error || "Passkey login is unavailable for this account.");
        return;
      }
      const { options, challengeKey } = (await optionsResponse.json()) as {
        options: Record<string, unknown>;
        challengeKey: string;
      };
      const authenticationResponse = await startAuthentication({
        optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
      });
      const verifyResponse = await fetch("/api/auth/browser/passkeys/login/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challengeKey,
          response: authenticationResponse,
        }),
      });
      if (!verifyResponse.ok) {
        const body = (await verifyResponse.json().catch(() => ({}))) as { error?: string };
        setError(body.error || "Passkey verification failed.");
        return;
      }
      const authData = (await verifyResponse.json()) as BrowserAuthResponse;
      markPasskeySetupCompleted();
      authRedirectStartedRef.current = true;
      await completeAuthResponse(authData);
      router.replace(redirectTarget);
    } catch (caughtError) {
      authRedirectStartedRef.current = false;
      console.error("Passkey login failed:", caughtError);
      setError("Passkey login failed. Try another sign-in method.");
    } finally {
      setPasskeyLoading(false);
    }
  }, [completeAuthResponse, email, redirectTarget, router]);

  const handleProviderRedirect = useCallback(
    (provider: string) => {
      const params = new URLSearchParams({
        email: normalizeEmailAddress(email),
        redirectTo: redirectTarget,
      });
      window.location.assign(`/api/auth/oauth/${provider}/start?${params.toString()}`);
    },
    [email, redirectTarget],
  );

  const handleMagicLinkContinue = useCallback(async () => {
    if (!magicLinkToken) {
      setError("Invalid or expired magic link. Please request a new one.");
      return;
    }

    await processMagicLink(magicLinkToken);
  }, [magicLinkToken, processMagicLink]);

  const handleReturnToLogin = useCallback(() => {
    router.replace("/login");
  }, [router]);

  if (previewingMagicLink || processingMagicLink) {
    const label = previewingMagicLink
      ? "Verifying your sign-in link…"
      : "Signing you in…";
    const progressLabel = previewingMagicLink
      ? "Checking the link is valid and not expired"
      : "Preparing your session";

    return (
      <div className="w-full">
        <h2
          className="text-3xl font-bold mb-8"
          style={{ color: "var(--color-alias-neutral-darkest)" }}
        >
          Sign-in link
        </h2>
        <div className="flex flex-col items-center gap-4 py-2">
          <motion.div
            className="w-8 h-8 border-4 rounded-full"
            style={{
              borderColor: "var(--color-alias-neutral-light)",
              borderTopColor: "var(--color-alias-info)",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 0.375, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
            aria-hidden
          />
          <p
            className="text-sm text-center max-w-sm"
            style={{ color: "var(--color-alias-neutral-dark)" }}
          >
            {label}
          </p>
          <LoginProgressIndicator active={true} label={progressLabel} className="w-56" />
        </div>
      </div>
    );
  }

  if (magicLinkToken) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full"
      >
        <h2
          className="text-3xl font-bold mb-8"
          style={{ color: "var(--color-alias-neutral-darkest)" }}
        >
          Confirm sign-in
        </h2>

        <div className="flex flex-col gap-3 -mt-2">
          <AnimatePresence initial={false}>
            {error ? (
              <motion.div
                key="magic-link-error"
                initial={{ opacity: 0, scaleY: 0.001 }}
                animate={{ opacity: 1, scaleY: 1 }}
                exit={{ opacity: 0, scaleY: 0.001 }}
                style={{ originY: 0 }}
              >
                <Banner variant="failure">{error}</Banner>
              </motion.div>
            ) : null}
          </AnimatePresence>

          {magicLinkPreview ? (
            <p
              className="text-xs"
              style={{ color: "var(--color-alias-neutral-darkest)" }}
            >
              This sign-in link is verified and ready. Continue to go to your account.
            </p>
          ) : null}

          <div className="flex flex-col gap-3 pt-1">
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleMagicLinkContinue()}
              disabled={!magicLinkPreview}
            >
              Continue
            </Button>
            <div className={styles.orDivider} aria-hidden>
              <span className={styles.orLabel}>or</span>
            </div>
            <Button
              type="button"
              variant="transparent"
              onClick={handleReturnToLogin}
            >
              Back to login
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  const formVariants = {
    enter: {
      opacity: 0,
      y: 20,
    },
    center: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
      },
    },
  };

  const showPasskeyCta = Boolean(loginOptions?.hasPasskey && passkeySupported);

  return (
    <AnimatePresence mode="wait" initial={true}>
      {!isExiting && (
        <motion.div
          key="email"
          variants={formVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="w-full"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-3xl font-bold mb-8" style={{ color: "var(--color-alias-neutral-darkest)" }}>
              Log in to SurveyLoop
            </h2>

            <div className="flex flex-col gap-6">
              <form className="contents" onSubmit={handleEmailStepSubmit}>
                <InputText
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="example@email.com"
                  value={email}
                  onChange={(event) => {
                    setEmail(normalizeEmailAddress(event.target.value));
                    setError("");
                    setMagicLinkSent(false);
                    setLoginOptions(null);
                    setStep("email");
                  }}
                  leadingVisual="Mail"
                />

                {step === "options" && loginOptions ? (
                  <div className="flex flex-col gap-3 -mt-2">
                    <p
                      className="text-xs"
                      style={{ color: "var(--color-alias-neutral-dark)" }}
                    >
                      {showPasskeyCta
                        ? "Use a passkey on this device, or scan a QR code to use one from your phone."
                        : "We'll email you a secure link that signs you in with one tap. Expires in 15 minutes."}
                    </p>

                    {showPasskeyCta ? (
                      <>
                        <Button
                          variant="primary"
                          leadingVisual="Fingerprint"
                          onClick={() => void handlePasskeyLogin()}
                          disabled={passkeyLoading}
                        >
                          {passkeyLoading ? "Signing in…" : "Sign in with passkey"}
                        </Button>
                        <div className={styles.orDivider} aria-hidden>
                          <span className={styles.orLabel}>or</span>
                        </div>
                      </>
                    ) : null}

                    <Button
                      variant={showPasskeyCta ? "secondary" : "primary"}
                      showStateBadge={true}
                      state={buttonState}
                      onStateChange={setButtonState}
                      stateLabels={magicLinkStateLabels}
                      disabled={passkeyLoading}
                      onClick={() => void handleMagicLinkRequest()}
                    >
                      {magicLinkStateLabels.idle}
                    </Button>

                    <LoginProgressIndicator
                      active={buttonState === "processing"}
                      label="Sending sign-in link..."
                    />

                    <AnimatePresence initial={false}>
                      {magicLinkSent ? (
                        <motion.div
                          key="login-info"
                          initial={{ opacity: 0, scaleY: 0.001 }}
                          animate={{ opacity: 1, scaleY: 1 }}
                          exit={{ opacity: 0, scaleY: 0.001 }}
                          style={{ originY: 0 }}
                        >
                          <Banner variant="success">
                            Magic link sent. Check your email for the login link.
                          </Banner>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>

                    {showPasskeyCta ? (
                      <Button
                        variant="secondary"
                        onClick={handleSwitchToPassword}
                        disabled={passkeyLoading || buttonState === "processing"}
                      >
                        Log in with Email / Password
                      </Button>
                    ) : (
                      <>
                        <div className={styles.orDivider} aria-hidden>
                          <span className={styles.orLabel}>or</span>
                        </div>
                        <div className="flex w-full justify-center">
                          <Button
                            variant="transparent"
                            leadingVisual="Key"
                            onClick={handleSwitchToPassword}
                            disabled={passkeyLoading || buttonState === "processing"}
                          >
                            Use password instead
                          </Button>
                        </div>
                      </>
                    )}

                    {loginOptions.providers.length ? (
                      <div className="flex flex-col gap-2">
                        {loginOptions.providers.map((provider) => (
                          <Button
                            key={provider}
                            variant="secondary"
                            onClick={() => handleProviderRedirect(provider)}
                          >
                            {provider === "google"
                              ? "Sign in with Google"
                              : provider === "apple"
                                ? "Sign in with Apple"
                                : "Sign in with Microsoft"}
                          </Button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <AnimatePresence initial={false}>
                  {error ? (
                    <motion.div
                      key="login-error"
                      initial={{ opacity: 0, scaleY: 0.001 }}
                      animate={{ opacity: 1, scaleY: 1 }}
                      exit={{ opacity: 0, scaleY: 0.001 }}
                      style={{ originY: 0 }}
                    >
                      <Banner variant="failure">{error}</Banner>
                    </motion.div>
                  ) : null}
                </AnimatePresence>

                {step === "email" ? (
                  <div className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={discovering || passkeyLoading}
                    >
                      {discovering ? "Checking sign-in options..." : "Continue"}
                    </Button>
                  </div>
                ) : null}
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <p>Loading...</p>
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  );
}
